import angular from "angular";

import {
  ApiListItem, Avatar, Calculation, Dates,
  EmploymentLogic,
  EmploymentRelationTaxcard,
  Iban,
  InsuranceContract,
  Overview, PensionContract, TaxCard2019Logic,
  Taxcards, Texts,
  WelcomeDataWorker,
  WorkerListItem,
  Workers
} from "@salaxy/core";

import {
  ReportsService,
  SettingsService,
  SessionService,
  UiCrudHelpers,
  UiHelpers,
} from "../../services";

/**
 * Provides methods for profiled Welcome messages to the end user.
 */
export class WelcomeController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "SessionService",
    "Workers",
    "SettingsService",
    "$location",
    "UiCrudHelpers",
    "$rootScope",
    "Taxcards",
    "Overview",
    "UiHelpers",
    "ReportsService",
  ];

  /**
   * If set to true, the settings are handled by proxy:
   * we do not ask the user to change the settings in
   * the Welcome screen (mainly Pension or Insurance).
   */
  public settingsByProxy;

  /** Worker home page UI properties */
  public workerUI: {
    isReady?: boolean,
    /** If true, worker has added taxcard. */
    taxCardAdded?: boolean,
  } = {};

  /** Welcome page (timeline) data forr the Worker role */
  public workerDataNew: WelcomeDataWorker;

  public workerList: WorkerListItem[] = [];


  /** Start date for filtering list of calculations with status "PaymentStarted". Displayed on front page */
  public paymentStartedWarningStartDate = Dates.getToday();
  /** End date for filtering list of calculations with status "PaymentStarted". Displayed on front page */
  public paymentStartedWarningEndDate =  Dates.add(this.paymentStartedWarningStartDate, -6, "month");

  private workerListStatus: "initial" | "loading" | "loaded" = "initial";

  private initialOwner: string;


  /**
   * Creates a new WelcomeController
   * @ignore
   */
  constructor(
    private session: SessionService,
    private workers: Workers,
    private settingsService: SettingsService,
    private $location: angular.ILocationService,
    private uiCrudHelpers: UiCrudHelpers,
    private $rootScope: angular.IRootScopeService,
    private taxcards: Taxcards,
    private overview: Overview,
    private uiHelpers: UiHelpers,
    private reportsService: ReportsService,
  ) { }

  /**
   * Implement IController
   */
  public $onInit = () => {
    this.loadWelcomeData();
    this.session.subscribe(this.$rootScope, () => {
      this.loadWelcomeData();
    });
  };


  /**
   * Gets a preview URL for the image.
   * Currently only for a taxcard, but may later support other items.
   */
  // eslint-disable-next-line
  public getPreviewUrl(item: ApiListItem) {
    // Previews disabled for every item 10.6.2021 /MJ
    return null;
  }

  /**
   * Returns the url where to download the tax card
   * @param taxCard Taxcard or if null, the current is used.
   */
  public getTaxcardDownloadUrl(taxCard): string {
    return this.taxcards.getDownloadUrl(taxCard);
  }

  /**
   * Moves the browser to create-new calculation screen
   * @param employmentId Identifier for the Employment relation for which to create the calculation.
   */
  public createNewCalculationFor = (employmentId: string): void => {
    this.$location.path("/calc/details/" + employmentId + "/new-for-employment");
  };

  /** Gets the current pension product or empty object */
  public getPension(): PensionContract {
    return this.settingsService.current?.calc.pensionContracts.find( (x) =>
     Dates.isSameOrAfter("today", x.startDate ?? "1900-01-01") &&
     Dates.isBefore("today", Dates.add(x.endDate ?? "2100-01-01", 1,"day"))) || {};
  }

  /** Gets the current insurance product or empty object */
  public getInsurance(): InsuranceContract {
    return this.settingsService.current?.calc.insuranceContracts.find( (x) =>
     Dates.isSameOrAfter("today", x.startDate ?? "1900-01-01") &&
     Dates.isBefore("today", Dates.add(x.endDate ?? "2100-01-01", 1,"day"))) || {};
  }

  /**
   * Loads the welcome data from the server.
   */
  public loadWelcomeData() {
    if (this.session.isSessionChecked && this.session.isAuthenticated) {
      const owner = this.session.session.currentAccount.owner;
      if (this.initialOwner != owner){
        this.initialOwner = owner;
        this.workerDataNew = undefined;
        this.workerUI = {};
        this.settingsByProxy = false;
        this.workerList = [];
        this.workerListStatus = "initial";

        if (this.session.isInRole("person")) {
          this.loadWorkerData();
        } else if (this.session.isInRole("company")) {
          this.loadCompanyData();
        }
      }
    }
    else {
      this.initialOwner = null;
      this.workerDataNew = undefined;
      this.workerUI = {};
      this.settingsByProxy = false;
      this.workerList = [];
      this.workerListStatus = "initial";
    }
  }

  /** Loads the Worker role data and UI. */
  public loadWorkerData() {
    this.overview.getWelcomeDataWorker().then((result) => {
      this.workerDataNew = result;
      this.workerUI = {
        isReady: true,
        taxCardAdded: !!result.activeTaxcard,
      };
    });
  }

  /** Loads worker list initially */
  public loadWorkers(): Promise<WorkerListItem[]> {
    if (this.workerListStatus === "loaded") {
      return Promise.resolve(this.workerList);
    } else {
      this.workerListStatus = "loading";
      // Only fetch first 50 workers (first page). Selection should not be shown if more than 49 workers / next page.
      return this.workers.getOData({}).then((data) => {
        this.workerList = data.items;
        this.workerListStatus = "loaded";
        return this.workerList;
      });
    }
  }

  /** Loads the welcome data from the server for Company role */
  public loadCompanyData() {
    // TODO
  }

  /**
   * Gets the URL for a calculation pdf.
   * If report has not been saved (ID is null), returns null.
   * @param calc - Calculation. This method requires that the calculation has been saved.
   * @param inline - if false, downloads the pdf file
   * @returns Url for specified report
   */
  public getPdfUrl(calc: Calculation, inline = false) {
    if (!calc) {
      return null;
    }

    return this.reportsService.getPdfUrlForCalc("salarySlip", calc.id, inline);
  }

  /**
   * Gets the chart data for taxcard, labels, colors and other settings.
   * Uses chartCache to avoid $digest loop.
   */
   public get taxcardChartData() {
    if (!this.workerDataNew || !this.workerDataNew.activeTaxcard) {
      return null;
    }
   if (!this.workerDataNew.activeTaxcard.card.incomeLimit){
      return {config: this.uiHelpers.cache(this, "pieChartV2",
      () => TaxCard2019Logic.getPieChartDataV2(this.workerDataNew.activeTaxcard),
      () => TaxCard2019Logic.getIncomeLimitInfo(this.workerDataNew.activeTaxcard)), limits:TaxCard2019Logic.getIncomeLimitInfo(this.workerDataNew.activeTaxcard)};
    }
    return {config: this.uiHelpers.cache(this, "lineChartV2",
    () => TaxCard2019Logic.getMainChartV2(this.workerDataNew.activeTaxcard),
    () => TaxCard2019Logic.getIncomeLimitInfo(this.workerDataNew.activeTaxcard)), limits:TaxCard2019Logic.getIncomeLimitInfo(this.workerDataNew.activeTaxcard)};
  }

  /**
   * Gets the chart data for taxcard, labels, colors and other settings.
   * Uses chartCache to avoid $digest loop.
   */
  public get taxcardMobileChartData() {
    if (!this.workerDataNew || !this.workerDataNew.activeTaxcard) {
      return null;
    }
    return this.uiHelpers.cache(this, "lineChartMobileV2",
      () => {
       const data = TaxCard2019Logic.getMainChartV2(this.workerDataNew.activeTaxcard);
       data.options.plugins.legend.display = false;
       // TODO ? data.options.scales.yAxes.display = false;
       return data;
      },
      () => TaxCard2019Logic.getIncomeLimitInfo(this.workerDataNew.activeTaxcard));
  }

  /** Calls the Worker wizard and if a Worker is added, starts a calculation for that worker. */
  public launchEmploymentWizard() {
    this.uiCrudHelpers.createNewEmployment().then((result) => {
      if (result.action === "ok") {
        this.createNewCalculationFor(result.item.id);
      }
    });
  }

  /** Gets the flags that indicate which tutorials to show. */
  public get tutorials(): {
    /** If true, shows info box that the site is runnin in anonymous mode. */
    showAnon: boolean,
    /** If true, should direct the user to create the first Worker */
    showAddWorker: boolean,
    /** If true, should direct user to create a new calculation.  */
    showAddCalculation: boolean,
    /** If true, should ask the user for digital signature. */
    showSign: boolean,
    /** If true, should ask the user to fill in the Pension contract information. */
    showPension: boolean,
    /** If true, should ask the user to fill in the Accident Insurance contract information. */
    showInsurance: boolean,
    /** If true, should promote Payroll feature. */
    showTryPayroll: boolean,
    /** If true, should promote new features. */
    showNewFeatures: boolean
  } {
    const result = {
      showAnon: false,
      showAddWorker: false,
      showAddCalculation: true,
      showSign: false,
      showPension: false,
      showInsurance: false,
      showTryPayroll: false,
      showNewFeatures: false
    };
    if (!this.session.isSessionChecked) {
      return result;
    }
    if (!this.session.isAuthenticated) {
      result.showAnon = true;
      return result;
    }
    if (!this.settingsByProxy) {
      if (!this.session.checkAccountVerification()) {
        result.showSign = true;
      }
      result.showPension = this.settingsService.current?.calc.pensionContracts.length == 0 || this.settingsService.current?.calc.pensionContracts.every( (x) => !x.company);
      result.showInsurance = this.settingsService.current?.calc.pensionContracts.length == 0 || this.settingsService.current?.calc.insuranceContracts.every( (x) => !x.company);
    }
    switch (this.workerListStatus) {
      case "loaded":
        if (this.workerList.length === 0) {
          result.showAddWorker = true;
          return result;
        }
        if (this.workerList.length > 45) {
          result.showAddCalculation = false;
          return result;
        }
        break;
      case "loading":
        return result;
      case "initial":
        this.loadWorkers();
        return result;
    }
    result.showAddCalculation = true;
    return result;
  }

  /**
   * Returns system alert html.
   */
  public get systemAlertHtml(): string {
    if (!this.session) {
      return null;
    }

    const userSession = this.session.getSession();
    if (!userSession) {
      return null;
    }

    const html = userSession.systemAlertHtml;
    if (!html) {
      return null;
    }
    return html;
  }

  /**
   * Returns true if system alert exists.
   */
  public get hasSystemAlert(): boolean {
    return !!this.systemAlertHtml;
  }

  /**
   * Filters preloaded workers.
   * Avatar values are formatted and ID is employment ID insteead of worker ID.
   */
  public filterWorkers = (searchString: string): any[] => {
    let searchResult = [];

    if (!searchString) {
      searchResult = this.workerList.slice(0, 30).map(this.getAvatar);
    } else {
      searchString = (searchString || "").trim().toLowerCase();
      searchResult = this.workerList
        .filter((w) =>
          (w.otherPartyInfo.avatar.displayName || "").toLowerCase().indexOf(searchString) >= 0 ||
          (w.otherPartyInfo.avatar.sortableName || "").toLowerCase().indexOf(searchString) >= 0 ||
          (w.otherPartyInfo.telephone || "").toLowerCase().indexOf(searchString) >= 0 ||
          (w.otherPartyInfo.email || "").toLowerCase().indexOf(searchString) >= 0 ||
          (w.id || "").toLowerCase().indexOf(searchString) === 0 ||
          (w.otherPartyInfo.officialId || "").toLowerCase().indexOf(searchString) === 0)
        .map(this.getAvatar);
    }

    return searchResult.map(item => {
      return {
        value: item.id,
        title: item.description,
        text: item.displayName + " (" + (item as any).displayId + ")",
        ui: item
      };
    });
  };

  /** Gets the display name for the selected worker. */
  public getWorkerName = (avatarId: string): string => {
    for (const worker of this.workerList) {
      if (avatarId === worker.otherId) {
        return Texts.escapeHtml(this.getAvatar(worker).displayName);
      }
    }
    return avatarId;
  };

  /**
   * Searches all workers and checks if manual/auto taxcard is enabled
   */
  public showAutoTaxcardInfobox(): boolean {
    if(this.workerList.length) {
      return !this.workerList.some(x => x.data.taxcard === EmploymentRelationTaxcard.Auto || x.data.taxcard === EmploymentRelationTaxcard.Manual);
    }
  }

  private getAvatar = (worker: WorkerListItem): Avatar => {
    const result: Avatar = Object.assign({}, worker.otherPartyInfo.avatar);
    (result as any).displayId =
      ((worker.id || "").startsWith("FI") ? Iban.formatIban(worker.id) + " / " : "")
      + (worker.otherPartyInfo.officialId ? worker.otherPartyInfo.officialId : "-")
      ;
    result.displayName = Texts.escapeHtml(result.displayName);
    result.description = Texts.escapeHtml(EmploymentLogic.getDescription(worker.otherPartyInfo, worker?.data.type, worker?.data.pensionCalculation));
    result.id = worker.id;
    return result;
  };
}